/**
 * OfficeSwitcher
 * ==============
 *
 * Top-nav dropdown that lets multi-office managers filter their views
 * to a single office (or see all). Only renders when the user has >1
 * accessible office; otherwise stays invisible so we don't clutter the
 * header for sales reps and single-office managers.
 *
 * Selection is URL-persisted via ``?office=<id>`` so a manager can send
 * a link like ``/loads?office=knot`` to a peer.
 */
import React, { useState, useRef, useEffect } from 'react';
import { Building2, ChevronDown, CheckCircle2 } from 'lucide-react';
import { useOfficeSwitcher } from '../contexts/OfficeSwitcherContext';

export default function OfficeSwitcher() {
  const {
    accessibleOffices,
    selectedOfficeId,
    setSelectedOfficeId,
    isMultiOffice,
  } = useOfficeSwitcher();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  // Close on outside click
  useEffect(() => {
    if (!open) return undefined;
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  if (!isMultiOffice) return null; // Nothing to switch — hide.

  const pickable = accessibleOffices.filter((o) => o.type === 'office');
  const active = pickable.find((o) => o.id === selectedOfficeId);
  const label = active ? active.name : `All ${pickable.length} offices`;

  return (
    <div className="relative" ref={wrapRef} data-testid="office-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={[
          'inline-flex items-center gap-1.5 h-8 px-2.5 text-xs font-medium rounded-md transition-colors border',
          active
            ? 'bg-teal-50 text-teal-800 border-teal-200 hover:bg-teal-100'
            : 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
        ].join(' ')}
        title="Filter dashboards & lists to one office"
        data-testid="office-switcher-toggle"
      >
        <Building2 className="h-3.5 w-3.5" />
        <span className="hidden md:inline max-w-[140px] truncate">{label}</span>
        <ChevronDown className="h-3 w-3" />
      </button>

      {open && (
        <div
          className="absolute right-0 top-full mt-1 w-64 bg-white border border-slate-200 rounded-md shadow-lg z-50 py-1"
          data-testid="office-switcher-menu"
        >
          <button
            type="button"
            onClick={() => { setSelectedOfficeId(null); setOpen(false); }}
            className={[
              'w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50',
              !selectedOfficeId ? 'bg-teal-50 text-teal-800 font-medium' : 'text-slate-700',
            ].join(' ')}
            data-testid="office-switcher-option-all"
          >
            {!selectedOfficeId
              ? <CheckCircle2 className="h-4 w-4 text-teal-600" />
              : <span className="w-4" />}
            <span className="flex-1">All my offices</span>
            <span className="text-[10px] text-slate-400">{pickable.length}</span>
          </button>
          <div className="border-t border-slate-100 my-1" />
          {pickable.map((o) => (
            <button
              key={o.id}
              type="button"
              onClick={() => { setSelectedOfficeId(o.id); setOpen(false); }}
              className={[
                'w-full text-left px-3 py-2 text-sm flex items-center gap-2 hover:bg-slate-50',
                selectedOfficeId === o.id
                  ? 'bg-teal-50 text-teal-800 font-medium'
                  : 'text-slate-700',
              ].join(' ')}
              data-testid={`office-switcher-option-${o.id}`}
            >
              {selectedOfficeId === o.id
                ? <CheckCircle2 className="h-4 w-4 text-teal-600" />
                : <Building2 className="h-4 w-4 text-slate-400" />}
              <span className="flex-1 truncate">{o.name}</span>
              {o.code && <span className="text-[10px] text-slate-400">{o.code}</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
